import React from "react";
import Layout from "../components/Layout";
import Seo from "../components/Seo";
import { Link } from "react-router-dom";
import { Mail, ArrowRight } from "lucide-react";

export default function About() {
  return (
    <Layout>
      <Seo
        title="About Dompryce — Honest domain price comparison"
        description="Dompryce helps founders and indie hackers find the cheapest domain registration across various hosts — without the renewal traps."
        path="/about"
      />
      <article className="mx-auto max-w-3xl py-8" data-testid="about-page">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">About</div>
        <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-zinc-900 md:text-5xl">
          We make buying domains less expensive — and less confusing.
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-zinc-600">
          Dompryce is an independent <strong>domain price comparison</strong> engine. We aggregate live registration and renewal pricing
          from the major hosts so you stop overpaying for your first domain — and stop getting hit by silent renewal hikes.
        </p>

        <section className="mt-12 space-y-8 text-base leading-relaxed text-zinc-700">
          <div>
            <h2 className="font-display text-2xl font-semibold text-zinc-900">Why we built this</h2>
            <p className="mt-3">
              The same .com can cost <strong>$7.49 at Spaceship</strong> and <strong>$21.99 at GoDaddy</strong>. Multiply that
              by 3 years of renewals and the difference is real money. We surface those numbers in one glance.
            </p>
          </div>
          <div>
            <h2 className="font-display text-2xl font-semibold text-zinc-900">What we track</h2>
            <ul className="mt-3 list-disc space-y-1 pl-6">
              <li>First-year registration price across various hosts</li>
              <li>Renewal pricing (often 2–3× the introductory rate)</li>
              <li>3-year locked-rate bundles and bundle discounts</li>
              <li>WHOIS privacy availability and policy fine print</li>
              <li>Support speed and trust score per registrar</li>
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl font-semibold text-zinc-900">How we make money</h2>
            <p className="mt-3">
              Some "Buy here" links carry an affiliate tag. Whether or not a registrar pays us a referral fee,{" "}
              <strong>the price we show is the price you pay</strong> and rankings are never influenced by who pays us more.
            </p>
          </div>
        </section>

        <div className="mt-12 rounded-2xl bg-zinc-900 p-8 text-white">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-300">
            <Mail className="h-4 w-4" /> Get in touch
          </div>
          <p className="mt-3 text-base text-zinc-300">
            Press, partnerships, registrar onboarding or just a friendly hello?
          </p>
          <a
            href="mailto:support@dompryce.com"
            className="mt-4 inline-flex items-center gap-2 font-display text-xl font-semibold hover:text-indigo-300"
            data-testid="about-contact-email"
          >
            support@dompryce.com <ArrowRight className="h-5 w-5" />
          </a>
        </div>

        <p className="mt-12 text-sm text-zinc-500">
          Ready to find a cheap domain? <Link to="/compare" className="font-medium text-indigo-600 hover:text-indigo-500">Run a price comparison →</Link>
        </p>
      </article>
    </Layout>
  );
}
